/**
 * W1-11b (app-shell-ui): the central dialog host (ARCHITECTURE.md §6.2; contract app/dialogs/types.ts).
 *
 * `services.ui.openDialog(DIALOG.x, props)` pushes an entry; the host mounts the registered `DialogDef.component`
 * with `{ services, props, close }` while the entry is open and the opener's promise resolves with what `close`
 * receives. Rules every dialog can rely on:
 *  - modal / sheet kinds: the component draws its own frame (`Dialog` / `Sheet` from src/ui, which know the title
 *    and the footer); its dismissal (Esc, close button, scrim) calls `close(null)`;
 *  - popover kind: the component draws its content only; the host draws the anchored surface next to the element
 *    that had focus (or the pointer) when it opened, and resolves `null` on Escape or a click outside. Focus moves
 *    into the surface once, when it appears (a control with `autoFocus` keeps it, one marked `data-autofocus` gets it,
 *    also when React re-runs effects in StrictMode development builds), stays where the user puts it
 *    while the app re-renders, and returns to the element that had it when the dialog was opened (else to the
 *    last editing region) when the surface closes, unless the user already moved it somewhere else;
 *  - the opener always gets an answer: `closeDialog(id)`, opening the same id again, the open project closing or
 *    being replaced (Library, browser Back, another project, a lock takeover: the host is mounted at the app level,
 *    so it watches `services.onChange`), the host unmounting and a crash inside the dialog (with a toast) all
 *    resolve `null`;
 *  - the popover surface is named by its first heading, else by the control that opened it;
 *  - `confirm` / `choose` of the UI service are drawn here as system dialogs (a `danger` confirm starts on Cancel).
 * While a modal dialog is open the `dialog` when-atom holds, so only bindings meant for dialogs fire.
 */

import { useEffect, useId, useLayoutEffect, useRef, useState, type ReactNode } from 'react';
import type { DocStore } from '../../state/types';
import { Button } from '../../ui/Button';
import { Dialog } from '../../ui/Dialog';
import type { AnyDialogDef, AppServices } from '../registry/types';
import type { DialogAnchor, DialogEntry, ShellUiService, SystemDialog, UiSnapshot } from '../uiService';
import { ErrorBoundary } from '../shell/ErrorBoundary';
import { useOptionalShell, useText, useUi, type ShellFocus } from '../shell/ShellContext';
import { m } from '../shell/shell.messages';
import s from '../shell/Shell.module.css';

const selectDialogs = (snap: UiSnapshot): readonly DialogEntry[] => snap.dialogs;

export interface DialogHostProps {
  readonly services: AppServices;
  readonly ui: ShellUiService;
  /** Force the `dialog` when-atom while a modal dialog is open (keyboard router). */
  readonly setAtom?: (atom: string, on: boolean) => void;
}

export function DialogHost(props: DialogHostProps) {
  const { services, ui, setAtom } = props;
  const dialogs = useUi(ui, selectDialogs);

  const modalOpen = dialogs.some(
    (d) => d.system !== null || (d.id !== null && services.registry.dialog(d.id)?.kind === 'modal'),
  );
  useEffect(() => {
    if (setAtom === undefined) return undefined;
    setAtom('dialog', modalOpen);
    return () => {
      setAtom('dialog', false);
    };
  }, [modalOpen, setAtom]);

  // Unmounting the host (app torn down) answers every open dialog with null.
  useEffect(
    () => () => {
      for (const d of ui.getSnapshot().dialogs) ui.resolveDialog(d.key, null);
    },
    [ui],
  );

  // The open project closed or was replaced: its dialogs (export, versions, a picker over its notes) and context
  // menu no longer apply. Opening a project from the Library keeps the Library's dialogs.
  useEffect(() => {
    let last: DocStore | null = services.store;
    return services.onChange(() => {
      const next = services.store;
      if (last !== null && next !== last) {
        ui.dismissDialogs();
        ui.closeContextMenu();
        ui.setGestureHints(null);
      }
      last = next;
    });
  }, [services, ui]);

  // A route change (the editor to Settings, the Library): popovers belong to the page that opened them and must
  // not float over the next one (W3-05 CR-5). Modals stay: they are the only thing on screen and their answers
  // still apply. The route is the hash (`NavService` does not expose a route stream).
  useEffect(() => {
    const onHashChange = (): void => {
      for (const d of ui.getSnapshot().dialogs) {
        if (d.id !== null && services.registry.dialog(d.id)?.kind === 'popover') ui.resolveDialog(d.key, null);
      }
      ui.closeContextMenu();
    };
    window.addEventListener('hashchange', onHashChange);
    return () => {
      window.removeEventListener('hashchange', onHashChange);
    };
  }, [services, ui]);

  return (
    <>
      {dialogs.map((entry) => (
        <ErrorBoundary
          key={entry.key}
          variant="silent"
          services={services}
          label={entry.id ?? 'system dialog'}
          onError={() => {
            ui.resolveDialog(entry.key, null);
            ui.toast({ key: m.dialogCrashed }, { kind: 'error' });
          }}
        >
          <HostedDialog entry={entry} services={services} ui={ui} />
        </ErrorBoundary>
      ))}
    </>
  );
}

function HostedDialog(props: {
  readonly entry: DialogEntry;
  readonly services: AppServices;
  readonly ui: ShellUiService;
}) {
  const { entry, services, ui } = props;
  const close = (result: unknown): void => {
    ui.resolveDialog(entry.key, result);
  };
  if (entry.system !== null) return <SystemDialogView dialog={entry.system} services={services} close={close} />;
  if (entry.id === null) return null;
  const def = services.registry.dialog(entry.id) as AnyDialogDef | undefined;
  if (def === undefined) return null;
  const content = renderDef(def, entry, services, close);
  if (def.kind === 'popover') {
    return (
      <AnchoredSurface
        anchor={entry.anchor}
        opener={entry.opener}
        fallbackLabel={services.i18n.t(m.popoverLabel)}
        onDismiss={() => {
          close(null);
        }}
      >
        {content}
      </AnchoredSurface>
    );
  }
  return <>{content}</>;
}

/** Calls the dialog component with its props (typed per id by DialogMap; the host only forwards them). */
function renderDef(
  def: AnyDialogDef,
  entry: DialogEntry,
  services: AppServices,
  close: (result: unknown) => void,
): ReactNode {
  const Component = def.component as (p: {
    readonly services: AppServices;
    readonly props: unknown;
    close(result: unknown): void;
  }) => ReactNode;
  return <Component services={services} props={entry.props} close={close} />;
}

// ---------------------------------------------------------------------------------------------------------------
// Popover surface
// ---------------------------------------------------------------------------------------------------------------

const MARGIN = 8;

/** Position of a surface of size (w, h) next to an anchor, inside the viewport. */
export function placeSurface(
  anchor: DialogAnchor | null,
  size: { readonly width: number; readonly height: number },
  viewport: { readonly width: number; readonly height: number },
): { readonly left: number; readonly top: number } {
  const a = anchor ?? { x: viewport.width / 2, y: viewport.height / 3, width: 0, height: 0 };
  let left = a.x;
  let top = a.y + a.height + 6;
  if (top + size.height > viewport.height - MARGIN) top = a.y - size.height - 6;
  if (top < MARGIN) top = Math.max(MARGIN, viewport.height - size.height - MARGIN);
  if (left + size.width > viewport.width - MARGIN) left = viewport.width - size.width - MARGIN;
  return { left: Math.max(MARGIN, left), top: Math.max(MARGIN, top) };
}

/**
 * First focus target of a surface without a focused child: a control marked `data-autofocus`, else the first
 * focusable one. (React's `autoFocus` prop focuses its element while mounting and renders no attribute, so it is
 * found through document.activeElement instead.)
 */
const FOCUSABLE = 'input, select, textarea, button, [tabindex]:not([tabindex="-1"]), [href], [contenteditable="true"]';

function firstFocusTarget(el: HTMLElement): HTMLElement {
  return el.querySelector<HTMLElement>('[data-autofocus]') ?? el.querySelector<HTMLElement>(FOCUSABLE) ?? el;
}

/** True when focus is nowhere in particular (the page body), or still inside `el` (being removed). */
function focusLost(el: Element | null): boolean {
  const active = document.activeElement;
  return (
    active === null ||
    active === document.body ||
    active === document.documentElement ||
    (el !== null && el.contains(active))
  );
}

const HEADING = 'h1, h2, h3, h4, h5, h6, [role="heading"], legend';
const NAMING_OPENER = 'button, [role="button"], [role="menuitem"], [role="tab"], a[href]';

/** Accessible name of the element that opened a popover, when it is a control (its label names the popover). */
function openerName(opener: Element | null): string | null {
  if (opener === null || !opener.matches(NAMING_OPENER)) return null;
  const label = opener.getAttribute('aria-label') ?? opener.getAttribute('title') ?? opener.textContent;
  const name = label.trim();
  return name === '' || name.length > 80 ? null : name;
}

/**
 * Names the popover surface (role="dialog"): by its first heading when the content has one (aria-labelledby),
 * else by the control that opened it, else a generic word.
 */
function nameSurface(el: HTMLElement, opener: Element | null, fallback: string, idBase: string): void {
  const heading = el.querySelector<HTMLElement>(HEADING);
  if (heading !== null) {
    if (heading.id === '') heading.id = `${idBase}-title`;
    el.setAttribute('aria-labelledby', heading.id);
    el.removeAttribute('aria-label');
    return;
  }
  el.removeAttribute('aria-labelledby');
  el.setAttribute('aria-label', openerName(opener) ?? fallback);
}

function AnchoredSurface(props: {
  readonly anchor: DialogAnchor | null;
  /** The element that had focus when the dialog was opened (recorded by the UI service). */
  readonly opener: Element | null;
  /** Accessible name when neither a heading inside nor the opener names the surface. */
  readonly fallbackLabel: string;
  readonly onDismiss: () => void;
  readonly children: ReactNode;
}) {
  const ref = useRef<HTMLDivElement>(null);
  const [pos, setPos] = useState<{ left: number; top: number } | null>(null);
  const { anchor } = props;
  // The latest handlers and focus targets, read by the listeners registered once on mount (the parent passes a new
  // `onDismiss` on every render; re-registering would move focus around while the user types).
  const dismiss = useRef(props.onDismiss);
  const opener = useRef(props.opener);
  const focus = useOptionalShell()?.focus ?? null;
  const shellFocus = useRef<ShellFocus | null>(focus);
  useLayoutEffect(() => {
    dismiss.current = props.onDismiss;
    opener.current = props.opener;
    shellFocus.current = focus;
  });

  // The accessible name follows the content (a heading may appear after the first render).
  const idBase = useId();
  useLayoutEffect(() => {
    const el = ref.current;
    if (el !== null) nameSurface(el, props.opener, props.fallbackLabel, idBase);
  });

  useLayoutEffect(() => {
    const el = ref.current;
    if (el === null) return;
    const place = (): void => {
      const r = el.getBoundingClientRect();
      setPos(
        placeSurface(
          anchor,
          { width: r.width, height: r.height },
          { width: window.innerWidth, height: window.innerHeight },
        ),
      );
    };
    place();
    // Re-place on window resize: the anchor and the free space move with the window (W3-05 CR-1, UX-044).
    window.addEventListener('resize', place);
    return () => {
      window.removeEventListener('resize', place);
    };
  }, [anchor]);

  useEffect(() => {
    const el = ref.current;
    // Initial focus, once: a child with autoFocus already has it.
    if (el !== null && !el.contains(document.activeElement)) {
      firstFocusTarget(el).focus({ preventScroll: true });
    }
    const onPointerDown = (e: PointerEvent): void => {
      if (el !== null && e.target instanceof Node && !el.contains(e.target)) dismiss.current();
    };
    document.addEventListener('pointerdown', onPointerDown, true);
    return () => {
      document.removeEventListener('pointerdown', onPointerDown, true);
      // Still in the document: not a close but React re-running the effect (StrictMode in development). Focus stays
      // where it is (on the autoFocus control), and the re-run finds it inside the surface.
      if (el?.isConnected === true) return;
      // Give focus back only when closing the surface lost it (the user may have clicked or tabbed elsewhere).
      if (!focusLost(el)) return;
      const back = opener.current;
      if (back instanceof HTMLElement && back.isConnected && (el === null || !el.contains(back))) {
        back.focus({ preventScroll: true });
      } else {
        shellFocus.current?.handBack();
      }
    };
  }, []);

  return (
    <div
      ref={ref}
      className={s.anchoredSurface}
      role="dialog"
      aria-modal="false"
      tabIndex={-1}
      style={pos === null ? { visibility: 'hidden', left: 0, top: 0 } : { left: pos.left, top: pos.top }}
      onKeyDown={(e) => {
        if (e.key === 'Escape' && !e.defaultPrevented) {
          e.preventDefault();
          e.stopPropagation();
          dismiss.current();
        }
      }}
    >
      {props.children}
    </div>
  );
}

// ---------------------------------------------------------------------------------------------------------------
// System dialogs (confirm / choose)
// ---------------------------------------------------------------------------------------------------------------

function SystemDialogView(props: {
  readonly dialog: SystemDialog;
  readonly services: AppServices;
  readonly close: (result: unknown) => void;
}) {
  const { dialog, close } = props;
  const text = useText(props.services);
  if (dialog.kind === 'confirm') {
    return (
      <Dialog
        open
        size="sm"
        title={text.tr(dialog.title)}
        description={text.tr(dialog.body)}
        onClose={() => {
          close(false);
        }}
        footer={
          <>
            {/* A destructive confirm starts on Cancel, so Enter or Space never destroys by accident. */}
            <Button
              variant="ghost"
              autoFocus={dialog.danger}
              onClick={() => {
                close(false);
              }}
            >
              {text.t(m.dialogCancel)}
            </Button>
            <Button
              variant={dialog.danger ? 'danger' : 'primary'}
              autoFocus={!dialog.danger}
              onClick={() => {
                close(true);
              }}
            >
              {text.tr(dialog.confirm)}
            </Button>
          </>
        }
      >
        {null}
      </Dialog>
    );
  }
  return (
    <Dialog
      open
      size="sm"
      title={text.tr(dialog.title)}
      {...(dialog.body === null ? {} : { description: text.tr(dialog.body) })}
      onClose={() => {
        close(null);
      }}
      footer={
        <Button
          variant="ghost"
          onClick={() => {
            close(null);
          }}
        >
          {text.t(m.dialogCancel)}
        </Button>
      }
    >
      <div className={s.chooseList} role="group" aria-label={text.t(m.chooseHint)}>
        {dialog.options.map((o, i) => (
          <button
            key={o.value}
            type="button"
            className={s.chooseOption}
            autoFocus={i === 0}
            onClick={() => {
              close(o.value);
            }}
          >
            <span className={s.chooseLabel}>{text.tr(o.label)}</span>
            {o.description !== undefined && <span className={s.chooseDetail}>{text.tr(o.description)}</span>}
          </button>
        ))}
      </div>
    </Dialog>
  );
}
